/**
 * Fetching, without a data library.
 *
 * Five screens and a handful of endpoints do not need a cache with
 * invalidation rules. What they do need is the three states named properly —
 * loading, failed, and loaded — because a screen that treats "not yet" and
 * "empty" as the same thing shows "nothing overdue" while it is still asking.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Options } from './api';

export type Result<T> =
  | { state: 'loading'; data: T | null }
  | { state: 'failed'; error: ApiError; data: T | null }
  | { state: 'loaded'; data: T };

export interface Fetched<T> {
  result: Result<T>;
  reload: () => void;
}

export function useApi<T>(path: string | null, options: Options = {}): Fetched<T> {
  const [result, setResult] = useState<Result<T>>({ state: 'loading', data: null });
  const [nonce, setNonce] = useState(0);

  // Stringified so a caller can pass an object literal without re-fetching on
  // every render, which is the classic way this hook becomes an infinite loop.
  const key = JSON.stringify(options.query ?? {});

  // Keeping the previous data through a reload is what stops the screen
  // flashing empty when somebody pulls to refresh.
  const previous = useRef<T | null>(null);

  useEffect(() => {
    if (path === null) return;

    const controller = new AbortController();
    setResult({ state: 'loading', data: previous.current });

    api<T>(path, {
      ...options,
      query: JSON.parse(key) as NonNullable<Options['query']>,
      signal: controller.signal
    })
      .then((data) => {
        previous.current = data;
        setResult({ state: 'loaded', data });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const error =
          err instanceof ApiError ? err : new ApiError(0, 'unknown', 'Something went wrong.', {});
        setResult({ state: 'failed', error, data: previous.current });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { result, reload };
}

/**
 * Running an operation that changes something.
 *
 * Separate from `useApi` because the states are not the same: a write is idle
 * until somebody asks for it, and its failure belongs next to the button that
 * caused it rather than replacing the screen.
 */
export function useAction<A extends unknown[]>(
  run: (...args: A) => Promise<unknown>
): {
  go: (...args: A) => Promise<boolean>;
  busy: boolean;
  error: ApiError | null;
  clear: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const go = useCallback(
    async (...args: A) => {
      setBusy(true);
      setError(null);
      try {
        await run(...args);
        return true;
      } catch (err) {
        setError(
          err instanceof ApiError ? err : new ApiError(0, 'unknown', 'Something went wrong.', {})
        );
        return false;
      } finally {
        setBusy(false);
      }
    },
    [run]
  );

  return { go, busy, error, clear: useCallback(() => setError(null), []) };
}
